/* KANON `Raporty` — `02-kto-ma-ktora.html`: the clients × surveys grid and the card beside it.
 *
 * ── A real `<table>`, and why it can be one here ─────────────────────────────────────────────
 * The archive and the roster both drew ARIA table roles over a CSS grid, because `table-layout:
 * fixed` reads a `<col>` width of `calc(…)` as `auto` and splits the flexible columns equally. That
 * objection does not apply: every track in this grid is a fixed pixel TOKEN, so the element that
 * associates a client's row with a survey's column can be the one the platform already has. In a
 * matrix that association is the whole point — a cell heard as "checkbox, checked" and nothing else
 * says which person and which form.
 *
 * ── `border-collapse: separate`, and the rules are SHADOWS ───────────────────────────────────
 * The canon's own. It is not cosmetic: in `collapse` mode the borders belong to the TABLE rather
 * than to the cell, so a `position: sticky` cell travels without its own border and the pinned
 * column loses the edge that tells a client's name from the first survey's ticks. `--rule-under`
 * and `--rule-right` are inset shadows for exactly that reason and paint in both states from one
 * declaration. `--rule-right` was added on this screen; `--rule-under` already existed and carries
 * the measurement.
 *
 * ── Three sticky positions, three z-indices ──────────────────────────────────────────────────
 * The head row sticks to the top, the client column to the left, and the corner cell to both — so
 * it has to paint over each of them. A sticky cell is opaque (`--bg-card`) because a transparent
 * fill paints the scrolling rows through itself; the roster's header band records the same finding.
 * Unlike the roster, the sticky lives on the CELL rather than on a rowgroup: a `<thead>` is the
 * containing block of its own row here, so the row would have no room to travel, and the `<td>`s of
 * the first column have no wrapper of their own at all.
 *
 * ── Fit-or-scroll ───────────────────────────────────────────────────────────────────────────
 * The card is the scrollport in BOTH axes and the table is exactly as wide as its own tracks. At
 * 1280 the shell's rail takes 64px, so the page can never gain a horizontal scrollbar for this grid;
 * it scrolls inside its own box instead, which is the archive's arrangement and its reason. When the
 * tracks add up to less than the card — one survey, or three — the table simply ends and the card's
 * remaining width is empty. That is the answer to a grid with nothing to scroll: leave the room.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

/* `min-width: 0` because this row is itself a flex ITEM of the tab panel, and its content — a table
 * two thousand pixels wide at fifteen columns — is what its default `min-width: auto` refuses to
 * shrink below. Without it the grid took the whole row and the card beside it was pushed off the
 * screen; photographed at 1440x900 before the line was added. */
.tab {
  display: flex;
  flex: 1;
  gap: var(--space-7);
  min-width: 0;
  min-height: 0;
}

.left {
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  min-height: 0;
}


/* The card, and the scrollport inside it. Two boxes rather than one: the card owns the ground, the
 * edge and the radius, and the inner box owns the overflow — a card that scrolled itself would clip
 * its own rounded corners against the sticky cells' square fills.
 *
 * ── `min-width: 0` on BOTH, and it is what makes the grid scroll rather than push ────────────
 * A flex item's default `min-width: auto` refuses to shrink below its content, and this card's
 * content is a table that is 2240px wide at the canon's fifteen columns. Without it the card took
 * all of that out of a 1440px row and the detail panel beside it was pushed clean off the screen —
 * photographed, not theorised. The width belongs to the TABLE; the scrollport's job is to be
 * narrower than it, and never to stretch it. */
.card {
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;

  /* The card HUGS the table (owner, 4 November 2026): eight clients and four surveys drew an 800px
   * card with the grid in its top-left corner. `max-width` keeps the scrollport for a roster wider
   * than the room. */
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
}


.scroll {
  flex: 0 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
}


.scroll:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* ── Every track is FIXED, and the table is exactly as wide as its tracks ────────────────────
 * `table-layout: fixed` with a `<col>` per column and `width: max-content`, which under fixed
 * layout resolves to the sum of those tracks. With ONE survey the table is 392px wide and stands at
 * the card's left edge with the rest of the card empty; with fifteen it is 2240px and the card is
 * the scrollport. The previous spelling was a 1240px FLOOR under an auto-laid-out table, and a
 * table with one flexible column hands it everything — the single column ran the width of the card
 * and its checkbox sat a thousand pixels from the head that named it (owner, 17 September 2026).
 * Auto layout also sized every column to its own longest cadence line: measured at fifteen columns
 * the tracks ran 112 to 176px, so the canon's even grid was never actually drawn.
 *
 * A `<col>` width must be a PLAIN token. `calc(…)` there is read as `auto` and fixed layout then
 * splits the flexible columns equally — the trap this file's earlier header cites against tables in
 * general and which is avoided rather than inherited. */
.table {
  width: var(--space-0);
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}

.colClient {
  width: var(--w-report-client);
}

.colSurvey {
  width: var(--w-report-matrix-column);
}

/* ── the head ─────────────────────────────────────────────────────────────────────────────── */

.headCell {
  position: sticky;
  top: var(--space-0);
  z-index: 2;
  height: var(--h-report-matrix-head);
  padding: var(--space-3) var(--space-14px);
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-align: start;
  vertical-align: top;
  background: var(--bg-card);
  box-shadow: var(--rule-under);
}


/* The corner. It sticks to both edges, so it paints over the other two. */
.headCorner {
  left: var(--space-0);
  z-index: 3;
  box-shadow: var(--rule-corner);
}

/* The head is a BUTTON: pressing a column describes it in the card. The whole cell is the target,
 * and the treatment is the cell's — a ghost or an icon button here would be a control inside a
 * control. */
.headButton {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  width: 100%;
  padding: var(--space-0);
  font: inherit;
  color: inherit;
  text-align: start;
  cursor: pointer;
  background: none;
  border: none;
}

.headButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* One line, clipped. Fifteen columns at a hundred-odd pixels is the canon's own case, and a title
 * that wrapped would make every head as tall as the longest one. The full title is on the button's
 * accessible name and in the card beside the grid, so nothing is lost to a reader. */
.headTitle {
  display: -webkit-box;
  overflow: hidden;
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  line-height: var(--type-label-lh);
  color: var(--text-2);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}


/* The described column wears the canon's own white title; the tint on its cells is below. Never
 * colour alone — the card beside the grid names the same survey in full. */
.headCellOn .headTitle {
  color: var(--text);
}

.headButton:hover .headTitle {
  color: var(--text);
}

/* Clipped on the same measure as the title above it, and for a sharper reason than tidiness: a
 * paused weekly form writes „co tydz · czw · wstrzymana", which is half as wide again as the track.
 * Under the auto layout this file used to have, that ONE cadence line set the width of its whole
 * column — measured at 176px against the canon's 132 — and under a fixed track it would run across
 * the neighbouring column's ticks instead. The whole line is on the button's accessible name and
 * the card beside the grid states it in full. */
.headCadence {
  display: block;
  overflow: hidden;
  font-size: var(--type-label);
  font-weight: var(--fw-regular);
  line-height: var(--type-label-lh);
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}


/* ── the rows ─────────────────────────────────────────────────────────────────────────────── */

/* The canon's `.td` is `padding:14px` on all four sides, not just the two. */
.cell {
  height: var(--h-report-matrix-row);
  padding: var(--space-0);
  text-align: center;
  box-shadow: var(--rule-under);
}


/* The client. A row header, so a screen reader names the person before the checkbox's own label. */
.rowHead {
  position: sticky;
  left: var(--space-0);
  z-index: 1;
  height: var(--h-report-matrix-row);
  padding: var(--space-0) var(--space-14px);

  /* The canon's row head is 14px, where this drew 12. */
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-align: start;
  background: var(--bg-card);
  box-shadow: var(--rule-corner);
}

.person {
  display: flex;
  gap: var(--space-10px);
  align-items: center;

  /* The one thing a surface may say about `shared/ui/AvatarChip` — see its own header. The canon
   * draws a 26px disc in this row. */
  --sz-avatar: 26px;
}

.personName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The described column, a hair above the card's ground. A tint at this strength is a hint about
 * where the eye is, not a state — which is why the head's ink and the card's own title carry the
 * fact. */
.cellOn {
  background: var(--hover-tint);
}

/* ── the checkbox ─────────────────────────────────────────────────────────────────────────── */

/* `position: relative` on the label so the input is positioned against its own control rather than
 * against whatever positioned ancestor is up the tree — `plan-library/ui/FilterPanel` measured what
 * happens otherwise. */
.check {
  position: relative;
  display: grid;
  place-items: center;

  /* The WHOLE cell is the target, not the 16px box it used to be. */
  width: 100%;
  height: var(--h-report-matrix-row);
  cursor: pointer;
}


.control {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
  clip-path: inset(50%);
}

.box {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  color: var(--on-accent);

  /* The canon's `.cb` edge is .3 of white, which is `--border-3` — `--border-2` is .20 and reads
   * as no box at all on the dark ground this grid is full of. */
  border: var(--border-thick) solid var(--border-3);
  border-radius: var(--radius-5px);

  /* White on the accent clears 3:1 only as bold text (system spec amendment, 2 November 2026). */
  font-weight: var(--fw-semibold);
}

.check:hover .box {
  border-color: var(--text-2);
}

.boxOn {
  background: var(--accent);
  border-color: var(--accent);
}

.control:focus-visible + .box {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* An EMPTY cell in a paused column, which `AssignCheckInAsync` refuses (`model/matrix.ts`'s
 * `cellRefusal`). Dashed rather than faded: a broken outline is a SHAPE difference, so it does not
 * ask the trainer to tell two greys apart, and the same box at the same weight keeps the grid's
 * rhythm. The reason itself is on the control's own `aria-describedby`, and the note under the card
 * states it in words when that column is the one being described. */
.boxRefused {
  border-style: dashed;
}

.checkRefused {
  cursor: not-allowed;
}

/* After the hover rule above, and at the same specificity, so a refused cell does not light up
 * under the pointer as though it were about to take the press. */
.check:hover .boxRefused {
  border-color: var(--border-2);
}

/* The write is in flight. Not optimistic — the tick has NOT moved — so the cell says that something
 * is happening to it without claiming an outcome. `aria-busy` on the input carries the same fact to
 * a reader. */
.boxBusy {
  opacity: 0.45;
}

.tick {
  width: 13px;
  height: 13px;
}

/* ── the foot ─────────────────────────────────────────────────────────────────────────────── */

/* No rule under the last band: it is the card's own edge. Tabular figures, because „6/8" under
 * „3/8" that do not line up read as a column of different widths rather than of counts. */
.footCell {
  height: var(--h-report-matrix-row);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--type-label);
  font-weight: var(--fw-regular);
  color: var(--text-2);
  text-align: center;
  font-variant-numeric: tabular-nums;
}


.footHead {
  position: sticky;
  left: var(--space-0);
  z-index: 1;
  text-align: start;
  white-space: nowrap;
  background: var(--bg-card);
  box-shadow: var(--rule-right);
}

/* ── the line under the card ──────────────────────────────────────────────────────────────── */

/* The line under the card. `--text-2`: it is a sentence the trainer has to READ — it says what a
 * tick does and what a paused column means — and `--text-3` at `--fs-label-sm` measured 3.01:1 on
 * the card and 2.37:1 on a sunken surface. */
.note {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: baseline;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text-2);
}

.noteText {
  flex: 1;
  min-width: 0;
}

/* The one place this tab spends its accent: a write that did not land. It is a fact the trainer has
 * to act on, and the word carries it — the ink is the second channel, never the only one. */
.noteFailed {
  flex: none;
  font-weight: var(--fw-bold);
  color: var(--danger-text-strong);
}

/* ── the states ───────────────────────────────────────────────────────────────────────────── */

/* The empty and error states stand INSIDE the card's reserve, so the box is the same height in
 * every one of them and shipping an answer changes the ink and not the geometry. */
.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: var(--h-report-matrix-min);
  padding: var(--space-7);
  text-align: center;
}

/* The wait. A head band and eight rows at the real heights, so the skeleton FITS the reserve it
 * stands in rather than making the card shrink when the answer lands. */
.skelHead {
  flex: none;
  height: var(--h-report-matrix-head);
  box-shadow: var(--rule-under);
}

.skelRow {
  display: flex;
  flex: none;
  gap: var(--space-14px);
  align-items: center;
  height: var(--h-report-matrix-row);
  padding: var(--space-0) var(--space-14px);
  box-shadow: var(--rule-under);
}

.skelName {
  width: 168px;
  height: 12px;
}

.skelTick {
  width: 20px;
  height: 20px;
}

.skelWait {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: var(--h-report-matrix-min);
}

/* ── the card beside the grid ─────────────────────────────────────────────────────────────── */

.panel {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-14px);
  width: var(--w-report-panel);
  align-self: flex-start;
  padding: var(--space-17px) var(--space-5);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

.panelHead {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  min-height: var(--h-report-chip);
}

.panelTitle {
  flex: 1;
  min-width: 0;
  margin: var(--space-0);
  overflow: hidden;
  font-size: var(--type-title-3);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}


.panelEdit {
  width: 28px;
  height: 28px;
  font-size: var(--fs-micro);
  background: transparent;
  border-radius: var(--radius-9-5px);
}

.panelEditIcon {
  width: var(--icon-xs);
  height: var(--icon-xs);
}

/* Both bands hold their height whether their words have arrived or not — the card's own reserve,
 * and what keeps it from growing 168px when the read lands (measured; see `MatrixCard`). */
.meta {
  display: flex;
  min-height: var(--h-report-chip);
  flex-wrap: wrap;
  gap: var(--space-10px);
  align-items: center;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.chip {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-report-chip);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--r-pill);
  white-space: nowrap;
}

.chipIcon {
  width: var(--sz-report-chip-icon);
  height: var(--sz-report-chip-icon);
}

/* „aktywna" in the canon's own green, „wstrzymana" in ordinary ink. The word is the fact; the ink
 * is a second channel and the paused case deliberately has none — a colour for „stopped" would make
 * a paused form look like a failure rather than a decision. */
.status {
  font-weight: var(--fw-regular);
  color: var(--text-2);
}


.statusPaused {
  font-weight: var(--fw-regular);
  color: var(--text-2);
}


.rule {
  flex: none;
  height: 1px;
  background: var(--border);
}

.sectionLabel {
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.assigned {
  display: flex;
  min-height: 17px;
  gap: var(--space-10px);
  align-items: center;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.questions {
  display: flex;
  flex-direction: column;
  gap: var(--space-9px);
  min-height: var(--h-report-question);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

/* A stated row height, so a skeleton bar and a question's words occupy exactly the same band — the
 * skeleton is what the card's height is BUILT from while the read is open, and a 11px bar in an
 * unconstrained row left the card 59px short of its loaded shape (measured). */
.question {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-height: 23px;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  color: var(--text);
}

.questionLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.questionIcon {
  flex: none;
  width: 13px;
  height: 13px;
  color: var(--text-2);
}

.allQuestions {
  align-self: flex-start;
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-decoration: none;
}


.panelState {
  display: grid;
  place-items: center;
  min-height: var(--h-report-detail-head);
}

.skelTitle {
  width: 168px;
  height: 14px;
}

.skelChip {
  width: 96px;
  height: 12px;
}

.skelQuestion {
  width: 160px;
  height: 11px;
}

/* The card's own quiet ink, for the sentence that stands where the questions would. */
.questionBlank {
  font-weight: var(--fw-medium);
  color: var(--text-3);
}

.allQuestions:hover {
  text-decoration: underline;
}

.allQuestions:focus-visible {
  border-radius: var(--r-chip);
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}
